<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'uploadingChange']);
const { t } = useI18n();
const fileInput = ref(null);
const isUploading = ref(false);
const MAX_IMAGE_SIZE = 4 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
];

const openFilePicker = () => {
  if (!props.disabled && !isUploading.value) fileInput.value?.click();
};

const uploadImages = async event => {
  const files = Array.from(event.target.files || []);
  event.target.value = '';
  if (!files.length) return;

  const invalidType = files.find(
    file => !ALLOWED_IMAGE_TYPES.includes(file.type)
  );
  if (invalidType) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.IMAGES.INVALID_TYPE'));
    return;
  }

  const oversized = files.find(file => file.size > MAX_IMAGE_SIZE);
  if (oversized) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.IMAGES.SIZE_ERROR'));
    return;
  }

  isUploading.value = true;
  emit('uploadingChange', true);
  try {
    const results = await Promise.allSettled(
      files.map(async file => {
        const result = await uploadFile(file);
        return {
          blob_id: result.blobId,
          url: result.fileUrl,
          filename: file.name,
          byte_size: file.size,
          content_type: file.type,
        };
      })
    );
    const uploaded = results
      .filter(result => result.status === 'fulfilled')
      .map(result => result.value);
    if (uploaded.length) {
      emit('update:modelValue', [...props.modelValue, ...uploaded]);
    }

    const failedResult = results.find(result => result.status === 'rejected');
    if (failedResult) throw failedResult.reason;
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('PILOT.KNOWLEDGE_BASES.FAQS.IMAGES.UPLOAD_ERROR')
    );
  } finally {
    isUploading.value = false;
    emit('uploadingChange', false);
  }
};

const removeImage = index => {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, imageIndex) => imageIndex !== index)
  );
};
</script>

<template>
  <div class="border-t border-n-weak px-3 py-3">
    <div v-if="modelValue.length" class="flex flex-wrap gap-2 mb-3">
      <div
        v-for="(image, index) in modelValue"
        :key="image.blob_id || image.id || image.url"
        class="relative size-20 overflow-hidden rounded-lg border border-n-weak bg-n-alpha-2"
      >
        <img
          :src="image.url"
          :alt="image.filename || 'FAQ图片'"
          class="size-full object-cover"
        />
        <button
          type="button"
          class="absolute right-1 top-1 flex size-6 items-center justify-center rounded-full bg-n-slate-12/80 text-white hover:bg-n-ruby-9"
          :disabled="disabled"
          @click="removeImage(index)"
        >
          <Icon icon="i-lucide-x" class="size-3.5" />
        </button>
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="image/png,image/jpeg,image/gif,image/webp"
      multiple
      hidden
      @change="uploadImages"
    />
    <button
      type="button"
      class="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-n-brand hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
      :disabled="disabled || isUploading"
      @click="openFilePicker"
    >
      <Spinner v-if="isUploading" :size="16" />
      <Icon v-else icon="i-lucide-image-plus" class="size-4" />
      {{
        isUploading
          ? t('PILOT.KNOWLEDGE_BASES.FAQS.IMAGES.UPLOADING')
          : t('PILOT.KNOWLEDGE_BASES.FAQS.IMAGES.ADD')
      }}
    </button>
    <span class="ml-2 text-xs text-n-slate-10">
      {{ t('PILOT.KNOWLEDGE_BASES.FAQS.IMAGES.HELP') }}
    </span>
  </div>
</template>
